import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add,
  Edit,
  HistoryEdu,
  MoreVert,
  PeopleAlt,
  Refresh,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { fetchPricingPlans } from '../services/billingApi';
import {
  fetchTenants,
  getAuthHeaders,
  fetchLookups,
  billingCycleToRegisterSubscription,
} from '../services/licensingApi';
import TenantSubscriptionsDrawer from './TenantAdmin/TenantSubscriptionsDrawer';

const API_ROOT = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/').replace(/\/?$/, '/');

const emptyEditForm = {
  tenantName: '',
  tenantEmail: '',
  tenantCountry: '',
  tenantPhone: '',
  tenantDesc: '',
  tenantStatus: 'ACTIVE',
  mfaEnabled: false,
};

const isCdxUser = () => {
  try {
    return localStorage.getItem('cdx_user') === 'true';
  } catch {
    return false;
  }
};

const emptyForm = {
  companyName: '',
  userEmail: '',
  userPassword: '',
  confirmPassword: '',
  userFirstName: '',
  userLastName: '',
  userAddress: '',
  country: '',
  userPhone: '',
  planName: 'Trial',
  pricingPlanId: '',
  subscription: 'MONTHLY',
  numberOfUsers: '',
  subscriptionStatus: 'ACTIVE',
  autoRenew: true,
  currency: 'USD',
  cdxUser: false,
  mfaEnabled: false,
};

const LICENSE_PRODUCT_BY_CODE = {
  HUB: 'HUB',
  SYNC_FILE: 'SYNC_FILE',
  DESIGN_SHARE: 'SYNC_FILE',
  AGENTIC_AI: 'AGENTIC_AI',
  AI_AGENT: 'AGENTIC_AI',
};

const licenseProductsFromBillingPlan = (plan) => {
  const out = [];
  for (const item of plan?.items || []) {
    const raw = String(item.productCode || item.product_code || '').toUpperCase();
    const code = LICENSE_PRODUCT_BY_CODE[raw];
    if (code && !out.includes(code)) out.push(code);
  }
  return out;
};

const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[#?!@$%^&*-]).{8,}$/;
const phonePattern = /^\d{10}$/;

const TENANTS_PAGINATION_KEY = 'cdx_tenants_pagination';

const readSavedPagination = () => {
  try {
    const raw = sessionStorage.getItem(TENANTS_PAGINATION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const pageSize = Number(parsed.pageSize) || 10;
      const page = Math.max(0, Number(parsed.page) || 0);
      return { page, pageSize };
    }
  } catch {
    /* ignore */
  }
  return { page: 0, pageSize: 10 };
};

/**
 * CDX Tenants — list tenants + register new tenant (with plan/products → licensing).
 */
const TenantAdmin = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [subscriptionsTenant, setSubscriptionsTenant] = useState(null);
  const [paginationModel, setPaginationModel] = useState(readSavedPagination);
  const [editOpen, setEditOpen] = useState(false);
  const [editTenantId, setEditTenantId] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');
  const [actionMenu, setActionMenu] = useState({ anchorEl: null, row: null });
  const [lookups, setLookups] = useState({});
  const [billingPlans, setBillingPlans] = useState([]);

  const closeActionMenu = () => setActionMenu({ anchorEl: null, row: null });

  const statusOptions = lookups.SUBSCRIPTION_STATUS || [];
  const currencyOptions = lookups.CURRENCY || [];
  const billingOptions = lookups.BILLING_CYCLE || [];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, lu, bp] = await Promise.all([
        fetchTenants(),
        fetchLookups({ grouped: true }),
        fetchPricingPlans(false).catch(() => []),
      ]);
      setLookups(lu && typeof lu === 'object' ? lu : {});
      setBillingPlans(Array.isArray(bp) ? bp : []);
      setRows(
        data.map((t) => ({
          id: t.tenantId,
          ...t,
        }))
      );
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Back from License: restore page for that tenant and reopen Subscriptions. */
  useEffect(() => {
    if (loading || !rows.length) return;
    const openId = searchParams.get('openSubscriptions');
    if (!openId) return;

    const idx = rows.findIndex((r) => String(r.tenantId) === String(openId));
    if (idx >= 0) {
      const pageSize = paginationModel.pageSize || 10;
      const page = Math.floor(idx / pageSize);
      const next = { page, pageSize };
      setPaginationModel(next);
      try {
        sessionStorage.setItem(TENANTS_PAGINATION_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      const row = rows[idx];
      setSubscriptionsTenant({
        tenantId: row.tenantId,
        tenantName: row.tenantName,
      });
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('openSubscriptions');
    setSearchParams(nextParams, { replace: true });
  }, [loading, rows, searchParams, setSearchParams, paginationModel.pageSize]);

  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);
    try {
      sessionStorage.setItem(TENANTS_PAGINATION_KEY, JSON.stringify(model));
    } catch {
      /* ignore */
    }
  };

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const openRegister = () => {
    setForm(emptyForm);
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditTenantId(row.tenantId);
    setEditForm({
      tenantName: row.tenantName || '',
      tenantEmail: row.tenantEmail || '',
      tenantCountry: row.tenantCountry || '',
      tenantPhone: row.tenantPhone || '',
      tenantDesc: row.tenantDesc || '',
      tenantStatus: (row.tenantStatus || 'ACTIVE').toUpperCase(),
      mfaEnabled: !!row.mfaEnabled,
    });
    setEditError('');
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editTenantId) return;
    if (!editForm.tenantName?.trim()) {
      setEditError('Company name is required');
      return;
    }
    if (!editForm.tenantEmail?.trim()) {
      setEditError('Admin email is required');
      return;
    }
    setEditSubmitting(true);
    setEditError('');
    try {
      const res = await fetch(`${API_ROOT}synctrack/tenant/${editTenantId}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantName: editForm.tenantName.trim(),
          tenantEmail: editForm.tenantEmail.trim(),
          tenantCountry: editForm.tenantCountry.trim(),
          tenantPhone: editForm.tenantPhone.trim(),
          tenantDesc: editForm.tenantDesc.trim(),
          tenantStatus: editForm.tenantStatus,
          mfaEnabled: !!editForm.mfaEnabled,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || data.error || `Update failed (${res.status})`);
      }
      toast.success('Tenant updated');
      setEditOpen(false);
      await load();
    } catch (e) {
      setEditError(e.message || 'Failed to update tenant');
      toast.error(e.message || 'Failed to update tenant');
    } finally {
      setEditSubmitting(false);
    }
  };

  const validate = () => {
    if (!form.companyName?.trim()) return 'Company name is required';
    if (!form.userEmail?.trim()) return 'Admin email is required';
    if (!passwordPattern.test(form.userPassword)) {
      return 'Password must be 8+ chars with upper, lower, number, and special (#?!@$%^&*-)';
    }
    if (form.userPassword !== form.confirmPassword) return 'Passwords do not match';
    if (!form.userFirstName?.trim() || !form.userLastName?.trim()) return 'First and last name are required';
    if (!form.userAddress?.trim()) return 'Address is required';
    if (!form.country?.trim()) return 'Country is required';
    if (!phonePattern.test(form.userPhone)) return 'Phone must be exactly 10 digits';
    if (!form.numberOfUsers || Number(form.numberOfUsers) <= 0) return 'Number of users must be > 0';
    if (billingPlans.length > 0 && !form.pricingPlanId) return 'Billing plan is required';
    return '';
  };

  const handleRegister = async () => {
    const v = validate();
    if (v) {
      setError(v);
      toast.error(v);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        companyName: form.companyName.trim(),
        userEmail: form.userEmail.trim(),
        userPassword: form.userPassword,
        confirmPassword: form.confirmPassword,
        userFirstName: form.userFirstName.trim(),
        userLastName: form.userLastName.trim(),
        userAddress: form.userAddress.trim(),
        country: form.country.trim(),
        userPhone: form.userPhone.trim(),
        applicationId: 1,
        planName: form.planName || 'Trial',
        pricingPlanId: form.pricingPlanId ? Number(form.pricingPlanId) : null,
        subscription: billingCycleToRegisterSubscription(form.subscription),
        numberOfUsers: Number(form.numberOfUsers),
        products: licenseProductsFromBillingPlan(
          billingPlans.find((p) => String(p.pricingPlanId) === String(form.pricingPlanId))
        ),
        subscriptionStatus: form.subscriptionStatus,
        autoRenew: !!form.autoRenew,
        currency: form.currency || 'USD',
        llms: [],
        cdxUser: !!form.cdxUser,
        mfaEnabled: !!form.mfaEnabled,
      };

      const res = await fetch(`${API_ROOT}synctrack/tenant/post`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        /* ignore */
      }

      if (!res.ok) {
        throw new Error(data.message || text || `Registration failed (${res.status})`);
      }

      toast.success(
        data.message
          ? `Tenant created. Note: ${data.message}`
          : `Tenant registered: ${data.companyName || form.companyName} (#${data.tenantId})`
      );
      setDialogOpen(false);
      await load();

      if (data.tenantId != null) {
        navigate(`/admin/license?tenantId=${data.tenantId}`);
      }
    } catch (e) {
      console.error(e);
      setError(e.message || 'Registration failed');
      toast.error(e.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: 'tenantId', headerName: 'ID', width: 70 },
      { field: 'licenseNumber', headerName: 'License #', width: 120 },
      { field: 'tenantName', headerName: 'Tenant / Company', flex: 1, minWidth: 180 },
      { field: 'tenantEmail', headerName: 'Admin email', flex: 1, minWidth: 180 },
      { field: 'tenantDbPath', headerName: 'Schema', width: 110 },
      { field: 'tenantCountry', headerName: 'Country', width: 110 },
      { field: 'tenantStatus', headerName: 'Status', width: 110 },
      {
        field: 'mfaEnabled',
        headerName: 'Login MFA',
        width: 110,
        sortable: false,
        valueGetter: (_value, row) => (row?.mfaEnabled ? 'On' : 'Off'),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title="More">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setActionMenu({ anchorEl: e.currentTarget, row: params.row });
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [navigate]
  );

  if (!isCdxUser()) {
    return (
      <Box p={3}>
        <Typography color="error">Tenant administration is available to CDX users only.</Typography>
      </Box>
    );
  }

  return (
    <Box p={2} sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <PeopleAlt color="primary" />
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Tenants
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Register tenants. Use ⋮ for subscriptions and edit.
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button startIcon={<Refresh />} variant="outlined" onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button startIcon={<Add />} variant="contained" onClick={openRegister}>
            Register tenant
          </Button>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          disableRowSelectionOnClick
          onRowDoubleClick={(params) =>
            setSubscriptionsTenant({
              tenantId: params.row.tenantId,
              tenantName: params.row.tenantName,
            })
          }
          sx={{ border: 'none' }}
        />
      </Box>

      <Menu
        anchorEl={actionMenu.anchorEl}
        open={Boolean(actionMenu.anchorEl)}
        onClose={closeActionMenu}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            const row = actionMenu.row;
            closeActionMenu();
            if (row) {
              setSubscriptionsTenant({
                tenantId: row.tenantId,
                tenantName: row.tenantName,
              });
            }
          }}
        >
          <ListItemIcon>
            <HistoryEdu fontSize="small" />
          </ListItemIcon>
          <ListItemText>Subscriptions</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const row = actionMenu.row;
            closeActionMenu();
            if (row) openEdit(row);
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit tenant</ListItemText>
        </MenuItem>
      </Menu>

      <TenantSubscriptionsDrawer
        open={Boolean(subscriptionsTenant)}
        onClose={() => setSubscriptionsTenant(null)}
        tenantId={subscriptionsTenant?.tenantId}
        tenantName={subscriptionsTenant?.tenantName}
      />

      <Dialog open={dialogOpen} onClose={() => !submitting && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1, typography: 'subtitle1', fontWeight: 650 }}>Register tenant</DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5 }}>
          {error && (
            <Typography color="error" variant="caption" display="block" mb={1}>
              {error}
            </Typography>
          )}
          <Grid container spacing={1.25}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                required
                label="Company / Tenant name"
                value={form.companyName}
                onChange={(e) => setField('companyName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                required
                label="Admin email"
                type="email"
                value={form.userEmail}
                onChange={(e) => setField('userEmail', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                required
                label="Password"
                type="password"
                value={form.userPassword}
                onChange={(e) => setField('userPassword', e.target.value)}
                helperText="8+ chars, upper, lower, number, special"
                FormHelperTextProps={{ sx: { mx: 0, mt: 0.25 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                required
                label="Confirm password"
                type="password"
                value={form.confirmPassword}
                onChange={(e) => setField('confirmPassword', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                required
                label="First name"
                value={form.userFirstName}
                onChange={(e) => setField('userFirstName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                required
                label="Last name"
                value={form.userLastName}
                onChange={(e) => setField('userLastName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                required
                label="Address"
                value={form.userAddress}
                onChange={(e) => setField('userAddress', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                required
                label="Country"
                value={form.country}
                onChange={(e) => setField('country', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                required
                label="Phone (10 digits)"
                value={form.userPhone}
                onChange={(e) => setField('userPhone', e.target.value.replace(/\D/g, '').slice(0, 10))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                required
                type="number"
                label="Licensed users"
                value={form.numberOfUsers}
                onChange={(e) => setField('numberOfUsers', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required size="small">
                <InputLabel>Billing plan</InputLabel>
                <Select
                  label="Billing plan"
                  value={form.pricingPlanId}
                  onChange={(e) => setField('pricingPlanId', e.target.value)}
                >
                  <MenuItem value="">Select billing plan</MenuItem>
                  {billingPlans.map((p) => (
                    <MenuItem key={p.pricingPlanId} value={String(p.pricingPlanId)}>
                      {p.planName} ({p.planCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Billing</InputLabel>
                <Select
                  label="Billing"
                  value={form.subscription}
                  onChange={(e) => setField('subscription', e.target.value)}
                >
                  {billingOptions.map((s) => (
                    <MenuItem key={s.code} value={s.code}>
                      {s.label || s.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.subscriptionStatus}
                  onChange={(e) => setField('subscriptionStatus', e.target.value)}
                >
                  {statusOptions.map((s) => (
                    <MenuItem key={s.code} value={s.code}>
                      {s.label || s.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Currency</InputLabel>
                <Select
                  label="Currency"
                  value={form.currency}
                  onChange={(e) => setField('currency', e.target.value)}
                >
                  {currencyOptions.map((s) => (
                    <MenuItem key={s.code} value={s.code}>
                      {s.code}
                      {s.label && s.label !== s.code ? ` — ${s.label}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControlLabel
                sx={{ m: 0, height: '100%', '& .MuiFormControlLabel-label': { typography: 'body2' } }}
                control={
                  <Switch
                    size="small"
                    checked={!!form.cdxUser}
                    onChange={(e) => setField('cdxUser', e.target.checked)}
                  />
                }
                label="CDX user"
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControlLabel
                sx={{ m: 0, height: '100%', '& .MuiFormControlLabel-label': { typography: 'body2' } }}
                control={
                  <Switch
                    size="small"
                    checked={!!form.autoRenew}
                    onChange={(e) => setField('autoRenew', e.target.checked)}
                  />
                }
                label="Auto renew"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                sx={{ m: 0, '& .MuiFormControlLabel-label': { typography: 'body2' } }}
                control={
                  <Switch
                    size="small"
                    checked={!!form.mfaEnabled}
                    onChange={(e) => setField('mfaEnabled', e.target.checked)}
                  />
                }
                label="Login MFA (all users)"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.45 }}>
                Plan locks products and unit prices until renewal. Period: start today; end +1 month or +1 year from Billing.
                After register, set connectors / flows / limits on License to create the first invoice; email via Invoices → Send.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25 }}>
          <Button size="small" onClick={() => setDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleRegister} disabled={submitting}>
            {submitting ? 'Registering…' : 'Register'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => !editSubmitting && setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit tenant</DialogTitle>
        <DialogContent dividers>
          {editError && (
            <Typography color="error" variant="body2" mb={2}>
              {editError}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            Profile + tenant MFA. Change products / plan under License.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Company / Tenant name"
                value={editForm.tenantName}
                onChange={(e) => setEditForm((f) => ({ ...f, tenantName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Admin email"
                type="email"
                value={editForm.tenantEmail}
                onChange={(e) => setEditForm((f) => ({ ...f, tenantEmail: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={editForm.tenantCountry}
                onChange={(e) => setEditForm((f) => ({ ...f, tenantCountry: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={editForm.tenantPhone}
                onChange={(e) => setEditForm((f) => ({ ...f, tenantPhone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={editForm.tenantStatus}
                  onChange={(e) => setEditForm((f) => ({ ...f, tenantStatus: e.target.value }))}
                >
                  <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                  <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                  <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
                  <MenuItem value="DISABLED">DISABLED</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                minRows={2}
                value={editForm.tenantDesc}
                onChange={(e) => setEditForm((f) => ({ ...f, tenantDesc: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editForm.mfaEnabled}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, mfaEnabled: e.target.checked }))
                    }
                  />
                }
                label={
                  editForm.mfaEnabled
                    ? 'Login MFA on — all users under this tenant require email code'
                    : 'Login MFA off — users only MFA if their personal flag is on'
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSubmitting}>
            {editSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantAdmin;
